import React, { useMemo, useState } from 'react';

/**
 * Table editor for the org's CSV config files — in practice entity_library.csv,
 * the file that gets edited most and the one whose download-edit-upload round trip
 * was the most painful.
 *
 * Parsing is deliberately small but does handle the two things that actually occur
 * in these files: quoted fields containing commas (product names with a comma in
 * the alias list) and comment lines starting with "#", which the pipeline's own
 * reader skips and which must survive a save or the file loses its documentation.
 */

const st = {
  bar: { display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap' },
  search: {
    padding: '5px 8px', fontSize: '12px', border: '1px solid #dee2e6',
    borderRadius: '4px', minWidth: '200px',
  },
  wrap: { maxHeight: '420px', overflow: 'auto', border: '1px solid #e9ecef', borderRadius: '6px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th: {
    position: 'sticky', top: 0, background: '#f8f9fa', textAlign: 'left', padding: '6px 8px',
    borderBottom: '1px solid #dee2e6', fontSize: '11px', textTransform: 'uppercase',
    letterSpacing: '0.4px', color: '#6c757d', zIndex: 1,
  },
  td: { padding: '2px 4px', borderBottom: '1px solid #f1f3f5' },
  input: {
    width: '100%', padding: '4px 6px', fontSize: '12px', border: '1px solid transparent',
    borderRadius: '3px', boxSizing: 'border-box', background: 'transparent',
  },
  btn: {
    padding: '4px 10px', fontSize: '11px', fontWeight: 600, border: '1px solid #dee2e6',
    borderRadius: '4px', cursor: 'pointer', background: '#fff', color: '#495057',
  },
  count: { fontSize: '11px', color: '#868e96' },
};

/**
 * Split one CSV line, honouring double-quoted fields.
 *
 * Deliberately does NOT trim. coromandel's entity_library.csv contains rows like
 * `Weed, Weeds Generic,_Weeds_Generic` where the entity name genuinely begins with
 * a space. Trimming here meant that merely opening the file and saving it renamed
 * those entities — a silent edit to the org's vocabulary, written straight to blob.
 */
export function splitCsvLine(line) {
  const out = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') { cur += '"'; i++; }  // escaped quote
      else inQuotes = !inQuotes;
    } else if (ch === ',' && !inQuotes) {
      out.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out;
}

/** Quote a field only when it needs it, so untouched files stay byte-similar. */
export function csvCell(value) {
  const v = value == null ? '' : String(value);
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

/**
 * Every row remembers the exact line it came from, on a NON-enumerable property.
 *
 * An untouched row is written back verbatim, so opening a file and saving it
 * cannot alter it. Real files needed this: drroof has rows with fewer fields than
 * the header (`Materials,Designer shingles`), which re-serialising turns into
 * `Materials,Designer shingles,` — harmless in isolation, but it means a save
 * rewrites hundreds of lines nobody touched, and a diff of the config file stops
 * being reviewable.
 *
 * Non-enumerable matters: the editor updates a cell with `{ ...row, [col]: value }`,
 * which drops the property, so an EDITED row automatically loses its verbatim copy
 * and gets re-serialised. Deleted rows take theirs with them, and added rows never
 * had one. No bookkeeping and no stale-index bugs.
 */
function withRaw(row, raw) {
  Object.defineProperty(row, '__raw', { value: raw, enumerable: false, writable: false });
  return row;
}

export function parseCsv(text) {
  const lines = String(text ?? '').split(/\r?\n/);
  const comments = [];
  const dataLines = [];
  lines.forEach(line => {
    if (!line.trim()) return;                                  // stray blank lines are dropped
    if (line.trimStart().startsWith('#')) comments.push(line);
    else dataLines.push(line);
  });
  if (dataLines.length === 0) return { columns: [], rows: [], comments, headerLine: '' };

  const headerLine = dataLines[0];
  const columns = splitCsvLine(headerLine);
  const rows = dataLines.slice(1).map(line => {
    const cells = splitCsvLine(line);
    return withRaw(Object.fromEntries(columns.map((c, i) => [c, cells[i] ?? ''])), line);
  });
  return { columns, rows, comments, headerLine };
}

export function toCsv({ columns, rows, comments, headerLine }) {
  const out = [...(comments || [])];
  out.push(headerLine ?? columns.map(csvCell).join(','));
  rows.forEach(r => {
    out.push(r.__raw !== undefined ? r.__raw : columns.map(c => csvCell(r[c])).join(','));
  });
  return out.join('\n') + '\n';
}

export default function CsvTableEditor({ text, onChange }) {
  const parsed = useMemo(() => parseCsv(text), [text]);
  const { columns, rows, comments } = parsed;
  const [query, setQuery] = useState('');

  const emit = next => onChange(toCsv({ ...parsed, ...next }));

  const visible = useMemo(() => {
    if (!query.trim()) return rows.map((r, i) => ({ row: r, index: i }));
    const q = query.toLowerCase();
    return rows
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => columns.some(c => String(row[c] ?? '').toLowerCase().includes(q)));
  }, [rows, columns, query]);

  const setCell = (index, col, value) =>
    emit({ rows: rows.map((r, i) => (i === index ? { ...r, [col]: value } : r)) });

  const addRow = () => {
    setQuery('');   // a new blank row would be filtered out of view otherwise
    emit({ rows: [...rows, Object.fromEntries(columns.map(c => [c, '']))] });
  };

  const removeRow = index => emit({ rows: rows.filter((_, i) => i !== index) });

  if (columns.length === 0) {
    return (
      <div style={{ fontSize: '12px', color: '#868e96' }}>
        This file has no header row yet — use Download / Upload to seed it.
      </div>
    );
  }

  return (
    <div>
      <div style={st.bar}>
        <input
          style={st.search}
          placeholder="Filter rows…"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        <button style={st.btn} onClick={addRow}>+ Add row</button>
        <span style={st.count}>
          {query.trim() ? `${visible.length} of ${rows.length} rows` : `${rows.length} rows`}
        </span>
      </div>

      <div style={st.wrap}>
        <table style={st.table}>
          <thead>
            <tr>
              {columns.map(c => <th key={c} style={st.th}>{c}</th>)}
              <th style={{ ...st.th, width: '36px' }} />
            </tr>
          </thead>
          <tbody>
            {visible.map(({ row, index }) => (
              <tr key={index}>
                {columns.map(c => (
                  <td key={c} style={st.td}>
                    <input
                      style={st.input}
                      value={row[c] ?? ''}
                      onChange={e => setCell(index, c, e.target.value)}
                      onFocus={e => { e.target.style.borderColor = '#adb5bd'; e.target.style.background = '#fff'; }}
                      onBlur={e => { e.target.style.borderColor = 'transparent'; e.target.style.background = 'transparent'; }}
                    />
                  </td>
                ))}
                <td style={st.td}>
                  <button style={st.btn} onClick={() => removeRow(index)} title="Remove row">×</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {comments.length > 0 && (
        <div style={{ fontSize: '11px', color: '#868e96', marginTop: '6px' }}>
          {comments.length} comment line{comments.length === 1 ? '' : 's'} at the top of this file are kept as they are.
        </div>
      )}
    </div>
  );
}
